// PR-10.2 — Player-facing read-only rounds view. Mounted as the "Omgångar"
// tab in /leagues/:id (LeagueDetail.tsx). Companion to PR-10.1's admin
// LeagueRoundsManager (CRUD); this component is render-only.
//
// Visual model:
//   - Sorted ascending by round_number
//   - Active round gets a highlighted ring + "Pågår nu" badge
//   - Upcoming rounds with an unfulfilled chain prereq show a lock icon
//     + "Öppnas efter omgång #N" text
//   - Completed rounds are dimmed (history)
//   - Respects league.rounds_visible_ahead (admin setting from PR-10.1)
//     via filterVisibleRounds() — players don't see all future rounds
//     beyond the league's heads-up window
//
// Status semantics: stored status is the source of truth (kept in sync
// by the PR-10.2 cron daily at 04:05). The natural-status hint shown
// in the admin view is intentionally NOT shown here — players should
// see the canonical state, not internal sync mismatches.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Lock, Calendar as CalendarIcon, Trophy } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  filterVisibleRounds,
  isRoundOpen,
  type LeagueRoundLite,
} from "@/lib/leagueRoundsHelpers";

interface LeagueRoundRow {
  id: string;
  league_id: string;
  round_number: number;
  name: string | null;
  start_date: string;
  end_date: string;
  status: "upcoming" | "active" | "completed";
  opens_after_round_id: string | null;
}

const today = (): string => new Date().toISOString().slice(0, 10);

const STATUS_VARIANT: Record<LeagueRoundRow["status"], "default" | "secondary" | "outline"> = {
  upcoming: "outline",
  active: "default",
  completed: "secondary",
};

export default function LeagueRoundsView({
  leagueId,
  roundsVisibleAhead,
}: {
  leagueId: string;
  /** Mirror of leagues.rounds_visible_ahead. null = show all. */
  roundsVisibleAhead: number | null | undefined;
}) {
  const { t } = useTranslation("admin");
  const { data: rounds = [], isLoading } = useQuery<LeagueRoundRow[]>({
    queryKey: ["league-rounds", leagueId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("league_rounds" as never)
        .select("id, league_id, round_number, name, start_date, end_date, status, opens_after_round_id")
        .eq("league_id", leagueId)
        .order("round_number", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as LeagueRoundRow[];
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (rounds.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          {t("leagues.rounds.playerEmpty", "Inga omgångar är schemalagda än")}
        </CardContent>
      </Card>
    );
  }

  const visible = filterVisibleRounds(rounds as unknown as LeagueRoundLite[], roundsVisibleAhead);
  const todayStr = today();

  return (
    <div className="space-y-3 py-2">
      {visible.map((vr) => {
        // `vr` is the LeagueRoundLite shape; we need the full row for
        // chain prereq lookup, so map back to the original record.
        const r = rounds.find((x) => x.round_number === vr.round_number)!;
        const prereq = r.opens_after_round_id
          ? rounds.find((x) => x.id === r.opens_after_round_id) ?? null
          : null;
        const isActive = r.status === "active";
        const isLocked =
          r.status === "upcoming" &&
          prereq != null &&
          prereq.status !== "completed";
        const naturallyOpen = isRoundOpen(r, prereq, todayStr);

        return (
          <Card
            key={r.id}
            className={
              isActive
                ? "ring-2 ring-primary/40 bg-primary/5"
                : r.status === "completed"
                  ? "opacity-70"
                  : ""
            }
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                <span>
                  {t("leagues.rounds.roundLabel", "Omgång")} #{r.round_number}
                </span>
                {r.name && (
                  <span className="text-sm text-muted-foreground font-normal">
                    {r.name}
                  </span>
                )}
                <Badge variant={STATUS_VARIANT[r.status]} className="text-xs">
                  {t(`leagues.rounds.status.${r.status}`, r.status)}
                </Badge>
                {isActive && (
                  <Badge variant="default" className="text-xs gap-1">
                    <Trophy className="h-3 w-3" />
                    {t("leagues.rounds.activeNow", "Pågår nu")}
                  </Badge>
                )}
                {isLocked && (
                  <Badge variant="outline" className="text-xs gap-1 text-muted-foreground">
                    <Lock className="h-3 w-3" />
                    {t("leagues.rounds.lockedAfter", "Öppnas efter omgång")} #{prereq!.round_number}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-xs text-muted-foreground inline-flex items-center gap-1">
                <CalendarIcon className="h-3.5 w-3.5" />
                {r.start_date} – {r.end_date}
              </p>
              {/* Helpful hint when stored status disagrees with the natural
                  open state — rare (cron resyncs daily at 04:05) but
                  possible during the window between the date crossing and
                  the next cron pass. */}
              {!isActive && naturallyOpen && !isLocked && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  {t(
                    "leagues.rounds.openingSoon",
                    "Den här omgången är inom datumintervallet — startas om kort med automatisk uppdatering.",
                  )}
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
